import { Empty } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useHandleContextMenu from "../hooks/useHandleContextMenu";
import { TOperationData } from "../types/operation";
import { TPhaseDataWithTasks } from "../types/phase";
import { TTaskData } from "../types/task";
import { TWellData } from "../types/well";
import { GetAntIcon } from "../utils/ant_icons";
import { Status, statusIcon } from "../utils/statusIcon";
import DropColumn from "./DropColumn";
import TaskCard from "./TaskCard";
import { useMultiAssetStore } from "../stores/multiAssetStore";
import { filterTasks } from "../utils/filterUtils";

function Swimlane({
	label,
	operation,
	phases,
	defaultOpen,
	extraComponent,
	groupedTasks,
}: {
	label: string;
	operation?: TOperationData & TWellData;
	phases?: TPhaseDataWithTasks[];
	defaultOpen?: boolean;
	extraComponent?: React.ReactNode;
	groupedTasks?: { status: string; tasks: TTaskData[]; droppable: boolean }[];
}) {
	const [open, setOpen] = useState(defaultOpen || false);
	const [columnData, setColumnData] = useState<
		{ status: string; tasks: TTaskData[]; droppable: boolean }[]
	>([]);

	const navigate = useNavigate();
	const { handleContextMenu } = useHandleContextMenu();

	const { searchValue, selectedFilters } = useMultiAssetStore();

	useEffect(() => {
		setColumnData(groupedTasks || []);
	}, [groupedTasks]);

	const filteredPhases: TPhaseDataWithTasks[] | undefined = useMemo(() => {
		if (!phases) return phases;

		let filtered = phases;

		// Apply search filter
		if (searchValue) {
			filtered = filtered.map((phase) => ({
				...phase,
				tasks: phase.tasks.filter((task) =>
					task.task_name.toLowerCase().includes(searchValue.toLowerCase())
				),
			}));
		}

		// Apply role and status filters
		if (selectedFilters && Object.keys(selectedFilters).length > 0) {
			filtered = filtered.map((phase) => ({
				...phase,
				tasks: filterTasks(phase.tasks, selectedFilters),
			}));
		}

		return filtered;
	}, [searchValue, phases, selectedFilters]);

	const filteredColumnData: {
		status: string;
		tasks: TTaskData[];
		droppable: boolean;
	}[] = useMemo(() => {
		if (!columnData) return columnData;

		let filtered = columnData;

		// Apply search filter
		if (searchValue) {
			filtered = filtered.map((group) => ({
				...group,
				tasks: group.tasks.filter((task) =>
					task.task_name.toLowerCase().includes(searchValue.toLowerCase())
				),
			}));
		}

		// Apply role and status filters
		if (selectedFilters && Object.keys(selectedFilters).length > 0) {
			filtered = filtered.map((group) => ({
				...group,
				tasks: filterTasks(group.tasks, selectedFilters),
			}));
		}

		return filtered;
	}, [searchValue, columnData, selectedFilters]);

	const updateColumnData = (initialTask: TTaskData, newTask: TTaskData) => {
		if (initialTask.closed !== newTask.closed) {
		}

		// remove initial task from its column
		const initialColumn = columnData.find((group) =>
			group.tasks.some((task) => task._id === initialTask._id)
		);
		if (initialColumn) {
			initialColumn.tasks = initialColumn.tasks.filter(
				(task) => task._id !== initialTask._id
			);
		}
		// add new task to its new column
		const newColumn = columnData.find((group) => {
			if (newTask.closed === true) {
				return group.status === "Closed";
			}
			return group.status === newTask.status;
		});
		if (newColumn) {
			newColumn.tasks.push(newTask);
		}
		setColumnData([...columnData]);
	};

	return (
		<>
			<div
				className="container-header"
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					marginBottom: !open ? "15px" : "0px",
				}}
			>
				<div
					className="panel-info"
					onClick={() => {
						setOpen(!open);
					}}
				>
					<span>{label}</span>
					{open ? GetAntIcon("up") : GetAntIcon("downarrow")}
				</div>
				{extraComponent}
			</div>
			{open &&
				filteredPhases &&
				filteredPhases.length > 0 &&
				// if all tasks in all phases are empty, show empty state
				(filteredPhases.every((phase) => phase.tasks.length === 0) ? (
					<div
						style={{ padding: "20px", alignItems: "center", height: "100%" }}
					>
						<Empty description="No tasks." />
					</div>
				) : (
					<div
						style={{
							display: "flex",
							gap: "30px",
						}}
					>
						{filteredPhases.map((phase) => (
							<div
								key={phase._id}
								className="project-details-container"
								style={{
									minWidth: "320px",
									maxWidth: "320px",
									height: "100%",
									display: "flex",
									flexDirection: "column",
								}}
							>
								{phase.tasks.map((task: TTaskData) => (
									<div
										key={task._id}
										className="task-item"
										onClick={() => {
											const url = operation
												? `${operation.operation_code}/${phase._id}/${task._id}`
												: `multi-asset/${phase._id}/${task._id}`;
											navigate(url, {
												replace: false,
												state: { task_id: task._id },
											});
										}}
										onContextMenu={(e: any) => {
											const url = operation
												? `${operation.operation_code}/${phase._id}/${task._id}`
												: `multi-asset/${phase._id}/${task._id}`;
											const location = `${window.location.href}/${url}`;
											handleContextMenu(e, { link: location });
										}}
									>
										<div>{task.task_name}</div>
										{statusIcon(task.status as Status)}
									</div>
								))}
							</div>
						))}
					</div>
				))}

			{open &&
				filteredColumnData &&
				filteredColumnData.length > 0 &&
				(filteredColumnData.every((group) => group.tasks.length === 0) ? (
					<div
						style={{ padding: "20px", alignItems: "center", height: "100%" }}
					>
						<Empty description="No tasks." />
					</div>
				) : (
					<div
						style={{
							display: "flex",
							gap: "30px",
						}}
					>
						{filteredColumnData.map((group) => (
							<DropColumn
								key={group.status}
								column={group}
								operation={operation}
								updateColumnData={updateColumnData}
							>
								{group.tasks
									.sort((a: any, b: any) => {
										if ("sequence_id" in a && "sequence_id" in b) {
											return a.sequence_id - b.sequence_id;
										}
										return 0;
									})
									.map((task: TTaskData) => (
										<TaskCard
											key={task._id}
											task={task}
											operation={operation}
										/>
									))}
							</DropColumn>
						))}
					</div>
				))}
		</>
	);
}

export default Swimlane;
